import type { Agent, Hook, Model } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { CREATE_PREDICTION_FUNCTION_ID } from '@/constants';
import {
  toActionId,
  toAgentId,
  toHookId,
  toModelId,
  useDeleteAction,
  useDeleteHook,
  useDeleteModel as useDeleteModelApi,
  useListActions,
  useListHooks,
  useUpdateAgent,
  useUpdateHook,
} from '@/hooks/api';
import { isEmpty } from '@/utils';

export type UseDeleteModelOnSuccess = (model: Model) => void;
export type UseDeleteModelOnError = ({ error }: { error: Error }) => void;
export type UseDeleteModelCallbackArgs = {
  agentId: string;
  modelId: string;
  onSuccess?: UseDeleteModelOnSuccess;
  onError?: UseDeleteModelOnError;
};
export type UseDeleteModelCallback = ({
  agentId,
  modelId,
  onSuccess,
  onError,
}: UseDeleteModelCallbackArgs) => Promise<Model>;

export type UseDeleteModelOpts = {
  onSuccess?: UseDeleteModelOnSuccess;
  onError?: UseDeleteModelOnError;
};

export const useDeleteModel = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseDeleteModelOpts = {}) => {
  const { data: { data: actions } = {} } = useListActions();
  const { data: { data: hooks } = {} } = useListHooks();

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: deleteAction,
    isPending: isPendingDeleteAction,
    isSuccess: isSuccessDeleteAction,
    error: errorDeleteAction,
  } = useDeleteAction();

  const {
    mutateAsync: deleteHook,
    isPending: isPendingDeleteHook,
    isSuccess: isSuccessDeleteHook,
    error: errorDeleteHook,
  } = useDeleteHook();

  const {
    mutateAsync: deleteModel,
    isPending: isPendingDeleteModel,
    isSuccess: isSuccessDeleteModel,
    error: errorDeleteModel,
  } = useDeleteModelApi();

  const {
    mutateAsync: updateHook,
    isPending: isPendingUpdateHook,
    isSuccess: isSuccessUpdateHook,
    error: errorUpdateHook,
  } = useUpdateHook();

  const deleteModelFn: UseDeleteModelCallback = useCallback(
    async ({ agentId, modelId, onSuccess: onSuccessFn, onError: onErrorFn }: UseDeleteModelCallbackArgs) => {
      const onSuccess: UseDeleteModelOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseDeleteModelOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const relatedActionIds =
          actions
            ?.filter((action) => {
              return action.functionId === CREATE_PREDICTION_FUNCTION_ID && action.config.modelId === modelId;
            })
            .map((action) => action.actionId) ?? [];

        const deletedHookIds: string[] = [];

        for (const hook of hooks ?? []) {
          const updates: Partial<Hook> = {};

          if (hook.trueActionId && relatedActionIds.includes(hook.trueActionId)) {
            updates.trueActionId = null;
          }

          if (hook.falseActionId && relatedActionIds.includes(hook.falseActionId)) {
            updates.falseActionId = null;
          }

          if (!isEmpty(updates)) {
            const afterUpdate = { ...hook, ...updates } as Hook;
            if ([afterUpdate.trueActionId, afterUpdate.falseActionId].every((id) => id == null)) {
              await deleteHook(toHookId(hook.hookId));
              deletedHookIds.push(hook.hookId);
            } else {
              await updateHook({ id: toHookId(hook.hookId), updates });
            }
          }
        }

        for (const actionId of relatedActionIds) {
          await deleteAction(toActionId(actionId));
        }

        const model = await deleteModel(toModelId(modelId));

        await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [
              ...agent.resourceIds.filter((id) => !relatedActionIds.includes(id) && !deletedHookIds.includes(id)),
            ],
          }),
        });

        onSuccess?.(model);

        return model;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        } else {
          onError?.({ error: new Error('An unknown error occurred') });
        }
        throw error;
      }
    },
    [onSuccessFnBase, onErrorFnBase, actions, deleteModel, updateAgent, hooks, deleteHook, updateHook, deleteAction]
  );

  return {
    deleteModel: deleteModelFn,
    isPending:
      isPendingDeleteHook ||
      isPendingUpdateHook ||
      isPendingDeleteAction ||
      isPendingDeleteModel ||
      isPendingUpdateAgent,
    isSuccess:
      isSuccessDeleteHook ||
      isSuccessUpdateHook ||
      isSuccessDeleteAction ||
      isSuccessDeleteModel ||
      isSuccessUpdateAgent,
    errors: [errorDeleteHook, errorUpdateHook, errorDeleteAction, errorDeleteModel, errorUpdateAgent].filter(
      (e) => !!e
    ),
  };
};
